import { useCallback, useEffect, useRef, useState } from "react";
import { C, wsUrl } from "../lib/api";

export function useTableSocket(tableId) {
  const [state, setState] = useState(null);
  const [status, setStatus] = useState("connecting");
  const [lastError, setLastError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [chat, setChat] = useState([]);
  const [attempt, setAttempt] = useState(0);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const offsetRef = useRef(0);

  useEffect(() => {
    let closed = false;
    let timer;
    const connect = () => {
      const ws = new WebSocket(wsUrl(tableId));
      wsRef.current = ws;
      ws.onopen = () => {
        setStatus("open");
        retryRef.current = 0;
        ws.send(JSON.stringify({ v: C.PROTOCOL_VERSION, type: C.WsClientEvent.JOIN_TABLE, payload: {}, ts: new Date().toISOString() }));
      };
      ws.onmessage = (ev) => {
        const msg = JSON.parse(ev.data);
        const T = C.WsServerEvent;
        if (msg.type === T.TABLE_STATE) {
          offsetRef.current = msg.payload.server_time_ms - Date.now();
          setState(msg.payload);
        } else if (msg.type === T.ERROR) {
          setLastError({ ...msg.payload, at: Date.now() });
        } else if (msg.type === T.ANTI_SHOVER_PENALTY) {
          setNotice({ kind: "ANTI_SHOVER", ...msg.payload, at: Date.now() });
        } else if (msg.type === T.SEAT_EJECTED) {
          setNotice({ kind: "EJECTED", ...msg.payload, at: Date.now() });
        } else if (msg.type === T.CHAT) {
          setChat((c) => [...c.slice(-(C.CHAT.HISTORY - 1)), msg.payload]);
        } else if (msg.type === T.CHAT_HISTORY) {
          setChat(msg.payload.messages);
        } else if (msg.type === T.FRIEND_REQUEST || msg.type === T.FRIEND_ACCEPTED || msg.type === T.TIMEBANK_USED || msg.type === T.JACKPOT_WON || msg.type === T.ACHIEVEMENT) {
          setNotice({ kind: msg.type, ...msg.payload, at: Date.now() });
        } else if (msg.type === T.AUTO_CHIPS) {
          setNotice({ kind: "AUTO_CHIPS", auto: msg.payload.kind, amount: msg.payload.amount, stack: msg.payload.stack, at: Date.now() });
        } else if (msg.type === T.TABLE_MOVED) {
          setNotice({ kind: "MOVED", ...msg.payload, at: Date.now() });
        } else if (msg.type === T.MAINTENANCE) {
          setNotice({ kind: "MAINTENANCE", ...msg.payload, at: Date.now() });
        } else if (msg.type === T.TABLE_CLOSED) {
          closed = true; // staff closed the table: no reconnect, TablePage sends everyone to the lobby
          setNotice({ kind: "CLOSED", ...msg.payload, at: Date.now() });
        }
      };
      ws.onclose = (e) => {
        setStatus("closed");
        if (e.code === 4429) { setNotice({ kind: "TOO_MANY", at: Date.now() }); return; } // connection cap / flood: no reconnect loop
        if (e.code === 4404 || e.code === 4410) { setStatus("gone"); setNotice({ kind: "GONE", code: e.code, at: Date.now() }); return; } // table no longer exists (closed / server restarted): back to the lobby
        if (closed) return;
        if (e.code === 4401) { setStatus("auth"); return; } // the sign-in expired: the screen says so, instead of retrying into nothing
        // Keep trying for as long as the player keeps the table open — an API restart or a dropped connection must
        // heal itself, never leave them staring at a dead screen.
        const delay = Math.min(5000, 500 * 2 ** retryRef.current++);
        setAttempt(retryRef.current);
        timer = setTimeout(connect, delay);
      };
    };
    connect();
    // back from a locked phone / another tab / a dead network: reconnect at once instead of waiting out the backoff
    const kick = () => {
      if (closed || wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return;
      clearTimeout(timer);
      retryRef.current = 0;
      connect();
    };
    const onVisible = () => { if (document.visibilityState === "visible") kick(); };
    window.addEventListener("online", kick);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      closed = true;
      clearTimeout(timer);
      window.removeEventListener("online", kick);
      document.removeEventListener("visibilitychange", onVisible);
      wsRef.current?.close();
    };
  }, [tableId]);

  const send = useCallback((type, payload = {}) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ v: C.PROTOCOL_VERSION, type, payload, ts: new Date().toISOString() }));
    }
  }, []);

  const serverNow = useCallback(() => Date.now() + offsetRef.current, []);
  return { state, status, attempt, lastError, notice, chat, send, serverNow };
}
